/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import {
  ComponentManagerFn,
  RequiredItem,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './MultiDateSelector';
import { baseFieldProps, fontStyleProps } from '../TextField/BaseField';

const MultiDateSelectorManager: ComponentManagerFn<'MultiDateSelector'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'MultiDateSelector'>(platform, 'MultiDateSelector'),
    type: 'MultiDateSelector',
    name: 'Multi Date Selector',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'TriggerOnChange',
          'TriggerOnBlur',
          'TriggerOnFocus',
          'TriggerAfterServerValidate',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      ...baseFieldProps,
      ...fontStyleProps,
      viewAttribute: {
        ...(baseFieldProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: ({ props }: RequiredItem<'StringColumn'>) => {
          return ['String'];
        },
      },
      minDateAttribute: {
        label: 'Min Date Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: ({ props }: RequiredItem<'DateColumn'>) => {
          if (props.showTime) return ['DateTime', 'StringDateTime'];
          return ['Date', 'DateTime', 'StringDateTime'];
        },
      },
      maxDateAttribute: {
        label: 'Max Date Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: ({ props }: RequiredItem<'DateColumn'>) => {
          if (props.showTime) return ['DateTime', 'StringDateTime'];
          return ['Date', 'DateTime', 'StringDateTime'];
        },
      },
      placeholder: {
        label: 'Placeholder',
        type: 'string',
      },
      showTime: {
        label: 'Show Time',
        type: 'boolean',
      },
      disablePast: {
        label: 'Disable Past',
        type: 'boolean',
        helpText: 'Enable this to disable the past days',
      },
      format: {
        label: 'Format',
        type: 'string',
        helpText: 'e.g. MM/dd/yyyy hh:mm a',
      },
      onChangeFunction: {
        label: 'On Change',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      onKeyDownFunction: {
        label: 'On Key Down',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      onFocusFunction: {
        label: 'On Focus',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      onBlurFunction: {
        label: 'On Blur',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      serverValidateOnChange: {
        label: 'Server Validate On Change',
        type: 'boolean',
        category: 'Data',
      },
      pickerType: {
        label: 'Picker Type',
        type: 'select',
        options: {
          multiple: 'Multiple',
          range: 'Range',
        },
        category: 'General',
      },
      storeAsStringDateTime: {
        label: 'Enable StringDateTime Format',
        type: 'boolean',
        helpText: 'Enable this option to format the date as a stringDateTime.',
      },
    },
    isPropertyVisible(prop): boolean {
      switch (prop) {
        case 'showTime':
          return false;
        default:
          return true;
      }
    },
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    icon: { iconName: 'calendar-edit' },
    category: 'Forms',
    description: `**Overview**<br/>
The Multi Date Selector is a specialized form field that allows users to pick more than one date (or a date range) in a single input.
Unlike the standard Date Picker which only supports a single value, this component handles lists of dates or start/end ranges, storing them as a delimited string. It is essential for use cases like "Select Preferred Interview Dates" (Multiple) or "Select Vacation Period" (Range).

**1. Picker Modes**<br/>
You can configure the fundamental behavior of the calendar popup.
* **Picker Type:**
    * **Multiple:** Allows the user to click individual, non-consecutive dates (e.g., Jan 1, Jan 5, Jan 12).
    * **Range:** Forces the user to select a contiguous block defined by a Start Date and an End Date (e.g., Jan 1 to Jan 5).

**2. Data Binding & Storage**<br/>
Because this component stores multiple values, it interacts with data differently than a standard date field.
* **View Attribute:** Selects the Data Store attribute to save the selection.
* **Data Type:** Must be **String**.
* **Format:** The component automatically serializes the array of dates into a string format for storage.
* **Store As StringDateTime:**
    * **Enabled:** Saves the values in a standard ISO StringDateTime format, ensuring compatibility with backend systems that expect full timestamps.

**3. Validation Constraints**<br/>
You can restrict the selectable range to prevent invalid data entry.
* **Disable Past:** If enabled, users cannot select any date prior to "Today."
* **Min / Max Date Attribute:**
    * **Purpose:** Bind these to other fields in your form (e.g., ProjectStart and ProjectEnd) to dynamically restrict the allowable range in this selector.
    * **Supported Types:** Date, DateTime, StringDateTime.

**4. Display Customization**<br/>
* **Format:** Define how the selected dates appear in the text box (e.g., MM/dd/yyyy).
* **Placeholder:** Text to display when no dates are selected (e.g., "Select ranges...").
* **Font Styles:** Includes standard font controls (Font Size, Font Weight, Font Color, Font Family) to match your form's design system.

**5. Interaction & Logic**<br/>
* **Controller Hooks:** Bind custom JavaScript functions to events for granular control (OnChange, OnFocus / OnBlur, OnKeyDown).
* **Triggers:** Supports standard child triggers like **TriggerOnChange** and **TriggerAfterServerValidate** (for backend validation calls).`,
  };
};

export default MultiDateSelectorManager;
